import { useRef, useEffect } from 'react'
import { X, Camera, Upload, FileText } from 'lucide-react'

interface QuickScanCameraProps {
  onCapture: (imageData: string) => void
  onClose: () => void
  pageNumber?: number  // For multi-page: which page we're capturing
  existingPages?: number  // How many pages already captured
}

export function QuickScanCamera({ onCapture, onClose, pageNumber = 1, existingPages = 0 }: QuickScanCameraProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Auto-trigger file input on mount
  useEffect(() => {
    // Small delay to ensure component is mounted
    const timer = setTimeout(() => {
      fileInputRef.current?.click()
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      onClose()
      return
    }

    // Convert to base64
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      onCapture(base64)
    }
    reader.onerror = () => {
      console.error('Error reading file')
      onClose()
    }
    reader.readAsDataURL(file)
  }

  const handleManualCapture = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="fixed inset-0 z-50 bg-surface-base flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-surface-card">
        <div>
          <h2 className="text-lg font-semibold">
            {existingPages > 0 ? `Page ${pageNumber}` : 'Scan Homework'}
          </h2>
          {existingPages > 0 && (
            <p className="text-sm text-gray-400">
              {existingPages} page{existingPages > 1 ? 's' : ''} captured
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
        <div className="text-center space-y-4">
          <div className="w-24 h-24 rounded-full bg-quest-primary/20 flex items-center justify-center mx-auto">
            {existingPages > 0 ? (
              <FileText className="w-12 h-12 text-quest-primary" />
            ) : (
              <Camera className="w-12 h-12 text-quest-primary" />
            )}
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              {existingPages > 0 ? `Capture Page ${pageNumber}` : 'Take a Photo'}
            </h3>
            <p className="text-gray-400 max-w-sm">
              {existingPages > 0
                ? 'Capture the next page of this document.'
                : "Point your camera at the homework paper. We'll analyze it automatically."
              }
            </p>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Manual buttons (shown if auto-trigger doesn't work) */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <button
            onClick={handleManualCapture}
            className="btn-primary flex items-center gap-2 px-6 py-3"
          >
            <Camera className="w-5 h-5" />
            Take Photo
          </button>
          <button
            onClick={() => {
              // Remove capture attribute for file picker
              if (fileInputRef.current) {
                fileInputRef.current.removeAttribute('capture')
                fileInputRef.current.click()
              }
            }}
            className="btn-secondary flex items-center gap-2 px-6 py-3"
          >
            <Upload className="w-5 h-5" />
            Choose File
          </button>
        </div>
      </div>

      {/* Footer hint */}
      <div className="p-4 text-center text-sm text-gray-500">
        Tip: Good lighting helps our AI analyze the document better
      </div>
    </div>
  )
}
